import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Mail, MapPin, MessageCircle } from "lucide-react";
import { LOGO_URL, NAV_LINKS, CONTACT } from "../lib/nav";

export function Footer() {
  return (
    <footer className="relative bg-ink text-paper" data-testid="site-footer">
      <div className="mx-auto max-w-[1440px] px-6 pb-10 pt-24 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-10 border-b border-paper/10 pb-14 md:flex-row md:items-end">
          <h2 className="font-serif text-4xl uppercase leading-[1.02] sm:text-5xl lg:text-6xl">
            Take your brand
            <br />
            <em className="text-blush">offline.</em>
          </h2>
          <Link to="/book" data-testid="footer-book-button" className="btn btn-blush">
            Book Your Space
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-12 pt-14 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="inline-block rounded-2xl bg-white p-4">
              <img src={LOGO_URL} alt="ESSANDTIALS logo" className="h-24 w-auto" data-testid="footer-logo" />
            </div>
            <div className="mt-6 rounded-2xl border border-paper/10 bg-paper/5 p-5" data-testid="footer-instagram-card">
              <p className="font-serif text-lg tracking-[0.14em]">ESSANDTIALS ✨</p>
              <p className="mt-2 text-sm leading-relaxed text-paper/70">
                From Online to In-Store.
                <br />
                Pop-Ups • Retail • Beauty Space
                <br />
                💄 Makeup | 💇🏽 Hair | 💅🏽 Nails
                <br />
                📍 Bermuda
                <br />
                ↓ Book Your Space
              </p>
              <a
                href={CONTACT.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-blush transition-colors hover:text-paper"
              >
                <Instagram size={14} strokeWidth={1.5} />
                @essandtials
              </a>
            </div>
          </div>

          <div className="md:col-span-3" data-testid="footer-visit">
            <p className="text-[11px] uppercase tracking-[0.28em] text-paper/50">Visit</p>
            <a
              href={CONTACT.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-paper/85 transition-colors hover:text-blush"
            >
              <MapPin size={15} strokeWidth={1.5} className="mt-1 shrink-0" />
              {CONTACT.address}
            </a>
            <p className="mt-2 pl-6 text-xs leading-relaxed text-paper/50">
              In the heart of Hamilton&rsquo;s busiest retail district.
            </p>
            <p className="mt-6 text-[11px] uppercase tracking-[0.28em] text-paper/50">Hours</p>
            <ul className="mt-3 space-y-1.5 text-sm text-paper/85">
              {CONTACT.hours.map((h) => (
                <li key={h.days} className="flex justify-between gap-4">
                  <span className="text-paper/60">{h.days}</span>
                  <span>{h.time}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2" data-testid="footer-contact">
            <p className="text-[11px] uppercase tracking-[0.28em] text-paper/50">Contact</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="inline-flex items-center gap-2 text-paper/85 transition-colors hover:text-blush"
                >
                  <Mail size={14} strokeWidth={1.5} />
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-paper/85 transition-colors hover:text-blush"
                >
                  <Instagram size={14} strokeWidth={1.5} />
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-paper/85 transition-colors hover:text-blush"
                >
                  <MessageCircle size={14} strokeWidth={1.5} />
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3" data-testid="footer-explore">
            <p className="text-[11px] uppercase tracking-[0.28em] text-paper/50">Explore</p>
            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-paper/70 transition-colors hover:text-blush">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/rules" className="text-paper/70 transition-colors hover:text-blush">
                  Policies
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 overflow-hidden rounded-3xl border border-paper/10" data-testid="footer-map">
          <iframe
            title="ESSANDTIALS — 39 Dundonald Street, Hamilton, Bermuda"
            src={CONTACT.mapsEmbed}
            className="h-64 w-full grayscale-[0.4]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-paper/10 pt-6 text-[11px] uppercase tracking-[0.22em] text-paper/40 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} ESSANDTIALS · Bermuda</p>
          <p>Shop. Create. Connect. Grow.</p>
        </div>
      </div>
    </footer>
  );
}
